import type { ReactNode } from 'react';
import { cx } from '../ui/cx.ts';
import { CustomerCompanyLine } from './identity/CustomerCell.tsx';

export interface CustomerSummaryFact {
  readonly label: string;
  readonly value: ReactNode;
  /** Right-aligned tabular mono (money, counts). */
  readonly num?: boolean;
  readonly emphasis?: boolean;
  readonly testId?: string;
}

interface Props {
  readonly testId?: string;
  /** Primary line (customer email). Truncates. */
  readonly title: string;
  /** Secondary line under the title (org name). Optional. */
  readonly subtitle?: string;
  /** Mono micro id line (account / org ids). Optional. */
  readonly idLine?: string;
  /** Chips rendered beside the title (brand chip, pending-transfer chip). */
  readonly badges?: ReactNode;
  /** 2-column fact grid. */
  readonly facts: readonly CustomerSummaryFact[];
  /** Free-form row under the facts (risk chips, owner line…). */
  readonly extra?: ReactNode;
  /** Action area at the bottom (buttons/links). Rendered in a `flex flex-col gap-2`. */
  readonly actions?: ReactNode;
  readonly className?: string;
  /** CRM company line above the title. Omitted until the lookup resolves. */
  readonly company?: { readonly orgId?: string | null; readonly accountId?: string | null };
}

export function CustomerSummaryCard({
  testId,
  title,
  subtitle,
  idLine,
  badges,
  facts,
  extra,
  actions,
  className,
  company,
}: Props): JSX.Element {
  return (
    <article
      data-testid={testId}
      className={cx(
        'flex flex-col gap-3 rounded-lg border border-line-soft bg-card p-3',
        className,
      )}
    >
      <div className="flex flex-wrap items-start gap-x-2 gap-y-1">
        {company !== undefined ? (
          <CustomerCompanyLine
            orgId={company.orgId}
            accountId={company.accountId}
            className="w-full"
          />
        ) : null}
        <span className="min-w-0 flex-1 truncate text-body font-medium text-paper">{title}</span>
        {badges !== undefined ? (
          <div className="flex shrink-0 flex-wrap gap-1">{badges}</div>
        ) : null}
        {subtitle !== undefined ? (
          <span className="w-full truncate text-dense text-paper-mid">{subtitle}</span>
        ) : null}
        {idLine !== undefined ? (
          <span className="w-full truncate font-mono text-micro text-paper-low">{idLine}</span>
        ) : null}
      </div>
      <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
        {facts.map((fact) => (
          <div
            key={fact.testId ?? fact.label}
            data-testid={fact.testId}
            className="flex min-w-0 flex-col gap-0.5"
          >
            <dt className="text-label text-paper-low">{fact.label}</dt>
            <dd
              className={cx(
                'm-0 min-w-0 break-words text-dense',
                fact.num === true && 'font-mono tabular-nums',
                fact.emphasis === true ? 'font-medium text-paper' : 'text-paper-mid',
              )}
            >
              {fact.value}
            </dd>
          </div>
        ))}
      </dl>
      {extra !== undefined ? <div className="flex flex-wrap gap-1">{extra}</div> : null}
      {actions !== undefined ? (
        <div className="flex flex-col gap-2 border-t border-line-soft pt-3">{actions}</div>
      ) : null}
    </article>
  );
}
